// s6 — детальный экран биомаркера (график + текущее значение + история + AI инсайты).
// Layout follows prototype #s6:
//   - Custom header: back + (title + subtitle) + share icon top right
//   - Current value block: large value + status dot + combined «В норме. Диапазон ...»
//   - Period selector
//   - Big chart
//   - Green «Что это означает» card (static description)
//   - Amber «Что значит ваш результат» card (BiomarkerInsightBlock, AI per-marker)
//   - History list with colored status dots
//   - Sticky bottom CTA «Поделиться графиком» (solid primary)
//
// Share: PNG-картинка через off-screen ShareCard + html2canvas (mini-sprint 6-sharing,
// docs/specs/sprint-6-sharing/stage-1-contracts.md).

import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'wouter';
import { ChevronLeft, Info, Lightbulb, Loader2, Share2 } from 'lucide-react';
import { useBiomarkerDetail } from '@/api/hooks/biomarkers';
import { clearMarkerOrigin, peekMarkerOrigin } from '@/lib/markerNavState';
import { MobileFrame } from '@/components/layout/MobileFrame';
import { StatusGlyph } from '@/components/dashboard/StatusGlyph';
import { CoachMark } from '@/components/onboarding/CoachMark';
import { BiomarkerDetailSkeleton } from '@/components/Skeletons';
import { Button } from '@/components/ui/button';
import { BiomarkerChart } from '@/components/dashboard/BiomarkerChart';
import { PeriodSelector } from '@/components/dashboard/PeriodSelector';
import { BiomarkerInsightBlock } from '@/components/biomarkers/BiomarkerInsightBlock';
import { ActionPlanBlock } from '@/components/biomarkers/ActionPlanBlock';
import { ShareCard } from '@/components/biomarkers/ShareCard';
import { useShareChart } from '@/hooks/useShareChart';
import { useBottomBarInset } from '@/hooks/useBottomBarInset';
import { shareFilename } from '@/lib/share';
import { formatRuDate } from '@/lib/chart-data';
import { capitalizeFirst, cn, formatDeltaValue, formatNumber, formatPct } from '@/lib/utils';
import { explainStatus } from '@/lib/statusExplain';
import {
  type DisplayTier,
  displayTier,
  DOT_BG_BY_TIER,
  STATUS_WORD_BY_TIER,
  showsOptimumText,
  VALUE_TEXT_BY_TIER,
} from '@/lib/biomarkerStatus';
import type {
  BiomarkerDetailDelta,
  BiomarkerDetailPoint,
  ClinicalStatus,
  DeltaColor,
  OptimumStatus,
  TimelinePeriod,
} from '@/api/types';

// HEAL-35: подпись — по ТИРУ, а не по clinical_status.
//
// Было: лейбл брался из clinical_status, а цвет рядом — из тира. На 🟠 выходило
// оранжевое значение и подпись «В норме.» в одной строке, и это читалось как
// ошибка приложения, а не как «в норме, но не оптимально» (жалоба по ИФР-1 и
// комментарий про лактат: «в норме, но выделен оранжевым»). Формально прежнее
// решение (§0/D1) было верным — значение действительно в пределах нормы, — но
// объяснение жило в блоке ниже, а противоречие било в глаза сразу.
//
// Теперь оранжевый называет себя сам, а «в пределах нормы» договаривается
// подписью под ним (OPTIMUM_HINT) — факт не потерян.
const STATUS_LABEL: Record<DisplayTier, string> = {
  norm: 'В норме.',
  attention: 'Требует внимания.',
  bad: 'Вне нормы.',
  'no-range': 'Без оценки.',
};

// HEAL-52a: прежняя однострочная подпись OPTIMUM_HINT переехала в explainStatus —
// теперь она не общая фраза, а объяснение с конкретными числами и стороной отклонения.

const DELTA_BG_BY_COLOR: Record<DeltaColor, string> = {
  good: 'bg-norm/10 text-norm',
  bad: 'bg-bad/10 text-bad',
  neutral: 'bg-muted text-gray-700',
};

export function BiomarkerDetail({ code }: { code: string }) {
  // HEAL-8: дефолт периода — «Все» (сразу вся история показателя), не «1 год».
  // Экран — route-страница, при возврате remount'ится → дефолт снова «Все» (req 5,
  // пользовательский выбор не персистим).
  const [period, setPeriod] = useState<TimelinePeriod>('all');
  const { data, isPending, isError } = useBiomarkerDetail(code, period);
  const [, setLocation] = useLocation();
  const { cardRef, share, isSharing } = useShareChart();
  const stickyRef = useRef<HTMLDivElement>(null);
  useBottomBarInset(stickyRef);

  // HEAL-9: «Назад» возвращает на экран-источник (Главная / Маркеры) через history.back() —
  // ровно туда, откуда открыли карточку (+ восстановление scroll источника в ScrollToTop).
  // Origin не выставлен (прямой заход / refresh) → безопасный фолбэк на /markers.
  //
  // render-path ЧИСТЫЙ: openedInApp читается pure-peek'ом (StrictMode дублирует render и
  // initializer — peek идемпотентен, ничего не мутирует). Сам сброс origin (one-shot) —
  // side-effect, поэтому в useEffect: его двойной mount в StrictMode идемпотентен, а к
  // моменту сброса openedInApp уже захвачен из peek.
  const [openedInApp] = useState(() => peekMarkerOrigin());
  useEffect(() => {
    clearMarkerOrigin();
  }, []);
  const handleBack = () => {
    if (openedInApp) window.history.back();
    else setLocation('/markers');
  };

  // §6: экран схлопывался в строку «Загрузка…» и через полсекунды
  // разворачивался обратно. Структура известна заранее — показываем её.
  if (isPending) {
    return (
      <MobileFrame>
        <DetailHeader title="Показатель" onBack={handleBack} />
        <BiomarkerDetailSkeleton />
      </MobileFrame>
    );
  }
  if (isError || !data) {
    return (
      <MobileFrame>
        <DetailHeader title="Показатель" onBack={handleBack} />
        <div className="flex flex-1 items-center justify-center p-6 text-center text-sm text-muted-foreground">
          Не удалось загрузить данные.
        </div>
      </MobileFrame>
    );
  }

  const canShare = data.points.length > 0;
  const handleShare = () => {
    if (!canShare) return;
    void share({
      filename: shareFilename(code, period),
      title: data.meta.canonical_name_ru,
    });
  };

  return (
    <MobileFrame className="pb-[calc(6rem_+_env(safe-area-inset-bottom))]">
      <DetailHeader
        title={capitalizeFirst(data.meta.canonical_name_ru)}
        subtitle={data.meta.description_short ?? null}
        onBack={handleBack}
        onShare={canShare ? handleShare : undefined}
        shareDisabled={isSharing}
      />

      <main className="flex flex-1 flex-col gap-4 overflow-y-auto px-4 pb-6">
        {data.current && (
          <CurrentValueBlock
            value={data.current.value_normalized}
            valueText={data.current.value_qualitative}
            unit={data.current.unit_normalized}
            status={data.current.clinical_status}
            optimumStatus={data.meta.optimum_status}
            normalRangeLabel={data.meta.normal_range_label}
            normalMin={data.meta.normal_min}
            normalMax={data.meta.normal_max}
            optimumRangeLabel={data.meta.optimum_range_label}
            optimumMin={data.meta.optimum_min}
            optimumMax={data.meta.optimum_max}
            delta={data.delta}
          />
        )}

        {/* Уровень 2, вторая подсказка. Условие — первое открытие графика, у
            которого точек ДВЕ И БОЛЬШЕ: текст говорит о линии и о том, куда
            показатель двигался, а при одной точке нет ни линии, ни движения
            (R-8). Про счётчик анализов условие не спрашивает — после пакетной
            загрузки он сразу больше единицы (T-4). */}
        {data.points.length > 1 && (
          <CoachMark id="dynamics" pointer="down" title="Первая динамика">
            Каждая точка — один ваш анализ. Линия показывает, куда двигался показатель со временем.
          </CoachMark>
        )}

        <PeriodSelector value={period} onChange={setPeriod} />

        <BiomarkerChart
          points={data.points}
          normalMin={data.meta.normal_min}
          normalMax={data.meta.normal_max}
          unit={data.meta.standard_unit}
          period={period}
        />

        <section className="rounded-surface border border-primary/20 bg-primary/5 p-4">
          <div className="mb-2 flex items-center gap-2">
            <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
              <Info className="h-3.5 w-3.5" strokeWidth={2.5} aria-hidden="true" />
            </div>
            <h2 className="text-sm font-semibold text-primary-text">Что это означает</h2>
          </div>
          <p className="whitespace-pre-line text-hl-secondary leading-relaxed text-foreground">
            {data.meta.description_verified_ru}
          </p>
        </section>

        <section className="rounded-surface border border-amber-200 bg-amber-50 p-4">
          <div className="mb-2 flex items-center gap-2">
            <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-amber-500 text-white">
              <Lightbulb className="h-3.5 w-3.5" strokeWidth={2.5} aria-hidden="true" />
            </div>
            <h2 className="text-sm font-semibold text-amber-800">Что значит ваш результат</h2>
          </div>
          {/* D2 (Q2): на 🟠 suboptimal показываем optimum_text ВМЕСТО normal-инсайта. */}
          {showsOptimumText(data.meta.optimum_status, data.meta.optimum_text) ? (
            <p className="whitespace-pre-line text-hl-secondary leading-relaxed text-foreground">
              {data.meta.optimum_text}
            </p>
          ) : (
            <BiomarkerInsightBlock code={code} headless />
          )}
        </section>

        <ActionPlanBlock code={code} />

        <section>
          <div className="mb-2 text-hl-small font-semibold uppercase tracking-wider text-muted-foreground">
            История измерений
          </div>
          <HistoryList points={data.points} unit={data.meta.standard_unit} />
        </section>
      </main>

      {/* fixed → escape'ит MobileFrame, поэтому safe-area-inset-bottom добавляем здесь сами,
          иначе CTA «срезается» у home-indicator.
          useBottomBarInset: на этом экране нижнего меню нет, панель здесь — эта
          кнопка, и плашка уведомления обязана вставать над ней. */}
      <div
        ref={stickyRef}
        className="fixed inset-x-0 bottom-0 z-sticky mx-auto max-w-md border-t border-border bg-background/95 px-4 pt-4 pb-[calc(1rem_+_env(safe-area-inset-bottom))] backdrop-blur"
      >
        <Button onClick={handleShare} disabled={!canShare || isSharing} className="w-full gap-2">
          {isSharing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />}
          {isSharing ? 'Готовим картинку…' : 'Поделиться графиком'}
        </Button>
      </div>

      {/* Off-screen карточка для html2canvas. Всегда смонтирована (D4): recharts рендерит
          асинхронно, к моменту тапа график готов. aria-hidden — не для скринридера. */}
      {canShare && (
        <div aria-hidden className="pointer-events-none fixed left-[-9999px] top-0">
          <ShareCard ref={cardRef} meta={data.meta} points={data.points} current={data.current} period={period} />
        </div>
      )}
    </MobileFrame>
  );
}

function DetailHeader({
  title,
  subtitle,
  onBack,
  onShare,
  shareDisabled,
}: {
  title: string;
  subtitle?: string | null;
  onBack: () => void;
  onShare?: () => void;
  shareDisabled?: boolean;
}) {
  return (
    <div className="flex h-14 flex-shrink-0 items-center gap-2 border-b border-border px-3">
      <button
        type="button"
        onClick={onBack}
        aria-label="Назад"
        className="flex h-9 w-9 items-center justify-center rounded-full hover:bg-muted"
      >
        <ChevronLeft className="h-5 w-5" />
      </button>
      <div className="min-w-0 flex-1">
        <div className="truncate text-base font-semibold leading-tight">{title}</div>
        {subtitle && (
          <div className="truncate text-hl-small leading-tight text-muted-foreground">{subtitle}</div>
        )}
      </div>
      {onShare && (
        <button
          type="button"
          onClick={onShare}
          disabled={shareDisabled}
          aria-label="Поделиться"
          className="flex h-9 w-9 items-center justify-center rounded-full hover:bg-muted disabled:opacity-40"
        >
          <Share2 className="h-5 w-5 text-gray-700" />
        </button>
      )}
    </div>
  );
}

function CurrentValueBlock({
  value,
  valueText = null,
  unit,
  status,
  optimumStatus,
  normalRangeLabel,
  normalMin,
  normalMax,
  optimumRangeLabel,
  optimumMin,
  optimumMax,
  delta,
}: {
  value: number;
  /** HEAL-33: качественный ответ — показываем слово вместо порядковой оценки. */
  valueText?: string | null;
  unit: string;
  status: ClinicalStatus;
  optimumStatus: OptimumStatus;
  normalRangeLabel: string;
  normalMin: number | null;
  normalMax: number | null;
  optimumRangeLabel: string | null;
  optimumMin: number | null;
  optimumMax: number | null;
  delta: BiomarkerDetailDelta | null;
}) {
  const tier = displayTier(status, optimumStatus);
  const explanation = explainStatus({
    tier,
    // У качественного показателя value_normalized — порядковая оценка 0/1/2,
    // сравнивать её с числовыми границами бессмысленно.
    value: valueText ? null : value,
    normalMin,
    normalMax,
    optimumMin,
    optimumMax,
  });
  return (
    <div className="border-b border-border pb-4 pt-1">
      <div className="mb-2 flex items-baseline gap-3">
        <div
          className={cn(
            'font-bold',
            // Слово вместо числа — кегль меньше, моноширинный шрифт не нужен.
            valueText ? 'text-2xl' : 'font-mono text-4xl',
            VALUE_TEXT_BY_TIER[tier],
          )}
        >
          {valueText ? capitalizeFirst(valueText) : formatNumber(value)}
        </div>
        {!valueText && <div className="text-base text-muted-foreground">{unit}</div>}
        {delta && (
          <span
            className={cn(
              'ml-auto rounded-full px-2 py-1 text-xs font-bold',
              DELTA_BG_BY_COLOR[delta.delta_color],
            )}
          >
            {delta.delta_value >= 0 ? '+' : ''}
            {formatDeltaValue(delta.delta_value)} ({delta.delta_value >= 0 ? '+' : ''}
            {formatPct(delta.delta_pct)}%)
          </span>
        )}
      </div>
      {/* HEAL-52a: было одной слепленной серой строкой — «Требует внимания. В
          пределах нормы, но не оптимально. Диапазон: 0.4 - 4». Пользователь читал
          её как противоречие: статус тревожный, а его значение внутри показанного
          диапазона. Теперь видно, ЧТО с чем сравнили и почему статус такой. */}
      <div className="flex items-start gap-2 text-sm text-muted-foreground">
        <StatusGlyph tier={tier} className="mt-1" />
        <div className="min-w-0 space-y-1">
          <strong className="text-foreground">{STATUS_LABEL[tier]}</strong>

          {normalRangeLabel !== '—' && (
            <RangeRow label={valueText ? 'Норма' : 'Норма лаборатории'} value={normalRangeLabel} />
          )}
          {/* На 🔴 второй диапазон не показываем: рядом с «вне нормы» оптимум —
              шум, он про тонкую настройку, а не про отклонение. */}
          {optimumRangeLabel && tier !== 'bad' && (
            <RangeRow label="Оптимум HealthLab" value={optimumRangeLabel} />
          )}

          {explanation && <p className="pt-0.5 leading-snug">{explanation}</p>}
        </div>
      </div>
    </div>
  );
}

function RangeRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-2 text-hl-secondary">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-foreground">{value}</span>
    </div>
  );
}

function HistoryList({ points, unit }: { points: BiomarkerDetailPoint[]; unit: string }) {
  const sorted = [...points].sort((a, b) => (a.measured_at < b.measured_at ? 1 : -1));
  if (sorted.length === 0) {
    return <p className="text-sm text-muted-foreground">Нет данных.</p>;
  }
  return (
    <div className="divide-y divide-border overflow-hidden rounded-surface border border-border bg-surface">
      {sorted.map((p) => {
        // HEAL-35: оптимум теперь приходит с каждой точкой, а не только с последней.
        // Прежде прошлые точки красились по одной норме — строка истории была
        // зелёной под оранжевой шапкой того же результата.
        const pointTier = displayTier(p.clinical_status, p.optimum_status);
        return (
        <div
          key={`${p.upload_id}-${p.measured_at}`}
          className="flex items-center gap-3 p-3"
        >
          <div
            aria-hidden
            className={cn('h-8 w-1.5 flex-shrink-0 rounded-full', DOT_BG_BY_TIER[pointTier])}
          />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold">
              {formatRuDate(p.measured_at)} ·{' '}
              <span className={cn(p.value_qualitative ? '' : 'font-mono', VALUE_TEXT_BY_TIER[pointTier])}>
                {p.value_qualitative
                  ? capitalizeFirst(p.value_qualitative)
                  : `${formatNumber(p.value_normalized)} ${unit}`}
              </span>
            </div>
            {/* D-11/R-10: до этого статус прошлого замера нёс ТОЛЬКО цвет
                полоски слева. В строке есть место для слова, и слово
                читается лучше значка. */}
            <div className="text-hl-small text-muted-foreground">
              {STATUS_WORD_BY_TIER[pointTier]}
              {p.lab_source ? ` · ${p.lab_source}` : ''}
            </div>
          </div>
        </div>
        );
      })}
    </div>
  );
}
